/**
 * GlobalChatSystem Sub-Components
 * 
 * This file contains reusable sub-components for the global chat system,
 * including attachment icons, file previews, and clickable attachments.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  FilePreviewItem,
  FilePreviewInfo,
  FileName,
  FileSize,
  RemoveButton,
  AttachmentItem,
  AttachmentName,
  AttachmentSize,
  BODY_PORTAL_Z_INDEX,
} from './styles';
import {
  formatFileSize,
  truncateFilename,
  getFileCategory,
  type FileCategory,
} from './utils';
import type { AttachmentIconProps, FilePreviewProps, ClickableAttachmentProps } from './types';

// ============================================================================
// Attachment Icon Component
// ============================================================================

/**
 * Renders an appropriate icon based on the file type.
 * Used in both file previews and message attachments.
 */
export const AttachmentIcon: React.FC<AttachmentIconProps> = ({ fileType, isUser }) => {
  // Use brand blue for user attachments, slate for AI attachments (both visible on light backgrounds)
  const strokeColor = isUser ? "#2196f3" : "#64748b";
  const category = getFileCategory(fileType);

  const iconStyle = { width: '100%', height: '100%' };

  switch (category) {
    case 'image':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      );
    case 'pdf':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    case 'document':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      );
    case 'spreadsheet':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      );
    case 'presentation':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <rect x="8" y="12" width="8" height="6" rx="1" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle}>
          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <polyline points="13 2 13 9 20 9" />
        </svg>
      );
  }
};

// ============================================================================
// File Preview Component
// ============================================================================

/**
 * Displays a file preview with icon, name, size, and remove button.
 * Used in the attachment preview panel.
 *
 * Image files (mime starts with "image/") render an inline thumbnail
 * via URL.createObjectURL, which is revoked on unmount to avoid blob
 * leaks. Non-image files fall back to the mime-type icon.
 */
export const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove }) => {
  const isImage = (file.type || '').startsWith('image/');
  const [thumbSrc, setThumbSrc] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isImage) return;
    let url: string | null = null;
    try {
      url = URL.createObjectURL(file);
      setThumbSrc(url);
    } catch {
      setThumbSrc(null);
    }
    return () => {
      if (url) {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      }
    };
  }, [file, isImage]);

  return (
    <FilePreviewItem>
      {isImage && thumbSrc ? (
        <div
          style={{
            width: '40px',
            height: '40px',
            flexShrink: 0,
            borderRadius: '6px',
            overflow: 'hidden',
            background: '#f3f3f1',
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <img
            src={thumbSrc}
            alt={file.name}
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>
      ) : (
        <div
          style={{
            width: '40px',
            height: '40px',
            flexShrink: 0,
            borderRadius: '6px',
            background: '#f3f3f1',
            border: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ width: '20px', height: '20px' }}>
            <AttachmentIcon fileType={file.type} isUser={false} />
          </div>
        </div>
      )}
      <FilePreviewInfo>
        <FileName title={file.name}>{truncateFilename(file.name)}</FileName>
        <FileSize>{formatFileSize(file.size)}{file.type ? ` · ${file.type}` : ''}</FileSize>
      </FilePreviewInfo>
      <RemoveButton onClick={onRemove} aria-label={`Remove ${file.name}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </RemoveButton>
    </FilePreviewItem>
  );
};

// ============================================================================
// Image Hover Wrapper Component
// ============================================================================

export const ImageHoverWrapper: React.FC<{
  src: string;
  alt?: string;
  children: React.ReactNode;
}> = ({ src, alt, children }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    setCoords({ x: e.clientX, y: e.clientY });
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setIsHovered(true), 300); // 300ms delay to prevent flashing
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setCoords({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsHovered(false);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'inline-flex', width: '100%', height: '100%' }}
      >
        {children}
      </div>
      {isHovered && coords && ReactDOM.createPortal(
        <div
          data-prest0n-portal="true"
          style={{
            position: 'fixed',
            // Default offset below cursor; adjust dynamically if too close to screen edges
            left: Math.min(coords.x + 15, window.innerWidth - 320),
            top: Math.min(coords.y + 15, window.innerHeight - 320),
            zIndex: BODY_PORTAL_Z_INDEX,
            pointerEvents: 'none',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2), 0 2px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(0,0,0,0.08)',
            padding: '4px',
            maxWidth: '300px',
            maxHeight: '300px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            animation: 'fadeIn 0.15s ease-out forwards',
          }}
        >
          <img
            src={src}
            alt={alt || 'preview'}
            style={{
              maxWidth: '100%',
              maxHeight: '290px',
              objectFit: 'contain',
              borderRadius: '8px',
              display: 'block'
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
};

// ============================================================================
// Clickable Attachment Component
// ============================================================================

/**
 * A clickable attachment that opens the file in a new tab.
 * Used in chat message bubbles to display attached files.
 */
export const ClickableAttachment: React.FC<ClickableAttachmentProps> = ({ file, isUser }) => {
  const handleClick = () => {
    // Create an object URL so the user can view/download the attachment
    const fileUrl = URL.createObjectURL(file);
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = file.name; // shows full filename in download dialog
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();

    // Best-effort cleanup
    setTimeout(() => URL.revokeObjectURL(fileUrl), 0);
  };

  return (
    <AttachmentItem
      isUser={isUser}
      onClick={handleClick}
      role="button"
      aria-label={`View attachment: ${file.name}`}
      title={file.name}
    >
      <div style={{ width: '20px', height: '20px', flexShrink: 0 }}>
        <AttachmentIcon fileType={file.type} isUser={isUser} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AttachmentName>{file.name}</AttachmentName>
        <AttachmentSize isUser={isUser}>{formatFileSize(file.size)}</AttachmentSize>
      </div>
    </AttachmentItem>
  );
};

// ============================================================================
// SVG Icons (Inline for use without imports)
// ============================================================================

/**
 * Paper clip / attachment icon
 */
export const AttachmentSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
);

/**
 * The photos entry point's icon (2026-09-11) — a picture frame with a sun and
 * a hillside, the near-universal "photo library" glyph.
 *
 * It sits beside the paperclip because on iOS the two open DIFFERENT pickers:
 * the paperclip's ~80-extension `accept` lands in the Files browser, and a
 * screenshot lives in Photos. Same geometry as AttachmentSvgIcon so the pair
 * reads as one toolkit rather than two conventions.
 */
export const PhotoSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
);

/**
 * Speaker icon — the per-message "read this aloud" control (2026-09-09).
 *
 * Same 24-box, stroke-2, round-cap geometry as its siblings so the meta row
 * stays visually uniform. A cone plus two arcs, not a filled blob: at 12px the
 * arcs are what make it read as SOUND rather than a triangle.
 */
export const SpeakerSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M11 5 6 9H3v6h3l5 4z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7" />
    <path d="M18.5 5.5a9 9 0 0 1 0 13" />
  </svg>
);

/**
 * Stop icon — the same control while a readout is playing. A square, because
 * "stop" is the one shape nobody has to learn.
 */
export const StopSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

/**
 * Microphone icon — dictation in the composer toolkit, beside the paperclip
 * (Miles 2026-09-09: "a small modern microphone … much like the attachment
 * button"). Capsule, stand and base, in the sibling geometry.
 */
export const MicSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </svg>
);

/**
 * Send / arrow icon
 */
export const SendSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

/**
 * Minimize / hide icon
 */
export const HideSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

/**
 * Add-people icon (user + plus) — header trigger for the thread add-people
 * modal (group threading P2c; relocated to the header 2026-07-19 feedback).
 */
export const AddPeopleSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="8.5" cy="7" r="4"></circle>
    <line x1="20" y1="8" x2="20" y2="14"></line>
    <line x1="23" y1="11" x2="17" y2="11"></line>
  </svg>
);

/**
 * Mention icon (@) — the per-thread "Prest0n replies only when mentioned"
 * toggle.
 *
 * Shipped reusing AddPeopleSvgIcon, which sat next to the actual add-people
 * trigger in the same header rail: two adjacent buttons, identical glyph,
 * different actions. Reported 2026-08-07: "It's the same as the Add People
 * icon, which doesn't look right and is a bit confusing."
 *
 * "@" is the symbol the feature is literally named after — the setting is
 * about whether an @Prest0n mention is required — so the control now says
 * what it does without needing the tooltip. Same 24-box, stroke-2, round-cap
 * geometry as its siblings so the rail stays visually uniform.
 */
export const MentionSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="4"></circle>
    <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"></path>
  </svg>
);

/**
 * Slashed mention icon (@ with a strike) — the summon toggle's OFF state
 * ("Prest0n replies to every message"; a mention is NOT required).
 *
 * Owner feedback 2026-08-07: the single @ glyph plus a subtle colour shift
 * could not communicate which mode the thread was in. State now lives in the
 * icon itself, mirroring the pin's filled/outline convention: plain @ =
 * mention required, slashed @ = requirement off. Same 24-box, stroke-2,
 * round-cap geometry as MentionSvgIcon above so the rail stays uniform.
 */
export const MentionOffSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="4"></circle>
    <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"></path>
    <line x1="4" y1="20" x2="20" y2="4"></line>
  </svg>
);

/**
 * Dock right icon (chevron pointing right)
 */
export const DockRightSvgIcon: React.FC<{ className?: string; isDocked?: boolean }> = ({ className, isDocked }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {isDocked ? (
      <>
        <polyline points="6 9 12 15 18 9" />
        <line x1="12" y1="4" x2="12" y2="20" />
      </>
    ) : (
      <>
        <polyline points="18 9 12 15 6 9" />
        <line x1="12" y1="4" x2="12" y2="20" />
      </>
    )}
  </svg>
);

/**
 * Expand / fullscreen icon (arrows pointing outward from corners)
 */
export const ExpandSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Top-left expand */}
    <polyline points="15 3 21 3 21 9" />
    <line x1="21" y1="3" x2="14" y2="10" />
    {/* Bottom-right expand */}
    <polyline points="9 21 3 21 3 15" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

/**
 * Collapse / exit fullscreen icon (arrows pointing inward to corners)
 */
export const CollapseSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Top-right collapse */}
    <polyline points="4 14 10 14 10 20" />
    <line x1="10" y1="14" x2="3" y2="21" />
    {/* Bottom-left collapse */}
    <polyline points="20 10 14 10 14 4" />
    <line x1="14" y1="10" x2="21" y2="3" />
  </svg>
);

/**
 * Right arrow icon for "dock right" action
 */
export const ArrowRightSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/**
 * Down arrow icon for "return to default view" action
 */
export const ArrowDownSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <polyline points="19 12 12 19 5 12" />
  </svg>
);

/**
 * Plus icon for adding files
 */
export const PlusSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

/**
 * Close / X icon
 */
export const CloseSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

/**
 * Chevron down icon for scroll-to-bottom button
 */
export const ChevronDownSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * Chevron up icon for show-older-messages button
 */
export const ChevronUpSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

/**
 * Pin icon for the header's active-thread pin toggle (round 3, 2026-08-01).
 * The SAME thumbtack shape the row/chip ThreadChipPinButton draws (its 12-box
 * path scaled to the 24 viewBox), so pin reads identically everywhere;
 * `filled` mirrors the row's pinned treatment (fill: currentColor).
 */
export const PinSvgIcon: React.FC<{ className?: string; filled?: boolean }> = ({ className, filled }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path
      d="M14.4 2.4 L21.6 9.6 L17.2 10.4 L12.8 14.8 L12 21.6 L7.6 17.2 L2.4 22.4 M7.6 17.2 L3.2 12.8 L10 12 L14.4 7.6 Z"
      fill={filled ? 'currentColor' : 'none'}
    />
  </svg>
);

/**
 * Archive-box icon for the header's active-thread archive action (round 3,
 * 2026-08-01) — the header rail's counterpart of the row ×. A box, not a
 * trash can: archiving keeps the conversation in Prest0n's memory.
 */
export const ArchiveBoxSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="21 8 21 21 3 21 3 8" />
    <rect x="1" y="3" width="22" height="5" />
    <line x1="10" y1="12" x2="14" y2="12" />
  </svg>
);

/**
 * Trash / clear icon for clearing chat history
 */
export const TrashSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 7h16" />
    <path d="M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7" />
    <path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
  </svg>
);

/**
 * Horizontal "more" (⋯) icon for the docked header's overflow menu trigger
 * (docked density round, 2026-08-01) — the rail's secondary actions fold
 * behind this one invariant button at docked width.
 */
export const MoreSvgIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
    className={className}
  >
    <circle cx="5" cy="12" r="1.7" />
    <circle cx="12" cy="12" r="1.7" />
    <circle cx="19" cy="12" r="1.7" />
  </svg>
);

// ============================================================================
// Processing Tooltip Component
// ============================================================================

interface ProcessingTooltipProps {
  /** Whether the tooltip is visible */
  visible: boolean;
  /** Reference to the button element to position near */
  anchorRef: React.RefObject<HTMLButtonElement>;
  /** Callback when tooltip is dismissed */
  onDismiss: () => void;
}

/**
 * A tooltip that appears when users try to send a message while processing is active.
 * Portal-rendered above the send button with auto-dismiss after 3 seconds.
 */
export const ProcessingTooltip: React.FC<ProcessingTooltipProps> = ({
  visible,
  anchorRef,
  onDismiss,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate position relative to anchor element
  useEffect(() => {
    if (!visible || !anchorRef.current) return;

    const updatePosition = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (anchor) {
        setPosition({
          top: anchor.top - 52, // Position above the button
          left: anchor.left + anchor.width / 2,
        });
      }
    };

    updatePosition();

    // Auto-dismiss after 3 seconds
    const timer = setTimeout(onDismiss, 3000);

    // Update position on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, anchorRef, onDismiss]);

  if (!visible) return null;

  const tooltipContent = (
    <div
      onClick={onDismiss}
      data-prest0n-portal="true"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        transform: 'translate(-50%, 0)',
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        color: '#fff',
        padding: '10px 16px',
        borderRadius: '10px',
        fontSize: '0.85rem',
        fontWeight: 500,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
        zIndex: BODY_PORTAL_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        animation: 'processingTooltipFadeIn 0.2s ease-out',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        userSelect: 'none',
      }}
      role="tooltip"
      aria-live="polite"
    >
      {/* Spinner icon */}
      <svg
        viewBox="0 0 24 24"
        style={{
          width: '16px',
          height: '16px',
          animation: 'processingSpinnerRotate 1s linear infinite',
        }}
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          opacity="0.3"
        />
        <path
          d="M12 2 A10 10 0 0 1 22 12"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
      <span>please wait while processing...</span>
      {/* Arrow pointing down to button */}
      <span
        style={{
          position: 'absolute',
          bottom: '-8px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: '8px solid #0f172a',
        }}
      />
    </div>
  );

  // Portal to body to avoid z-index and overflow issues
  return ReactDOM.createPortal(tooltipContent, document.body);
};

